<template>
  <v-container class="contact-page" fluid>
    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Contact Form <span class="thai">ข้อมูลการติดต่อ</span></h1>
        <p class="subhead mb-0">Manage inquiries, feedback and complaints submitted from hospital branch websites.</p>
      </div>
    </div>

    <div class="tabs mb-4">
      <button
        v-if="canViewSubmissionsTab"
        :class="['tab', {'active': isSubmissionsTab}]"
        @click="goToSubmissionsTab"
      >
        Submissions
      </button>
      <button :class="['tab', {'active': isRoutingTab}]">Email Routing Configuration</button>
      <button
        v-if="canViewHospitalContactInfoTab"
        :class="['tab', {'active': isHospitalInfoTab}]"
        @click="goToHospitalInfoTab"
      >
        Hospital Contact Info
      </button>
    </div>

    <v-card class="table-card">
      <div class="routing-head">
        <div>
          <div class="rules-title">Routing Rules</div>
          <div class="rules-sub">Route form submissions to the right inbox by branch and inquiry type.</div>
        </div>
        <v-btn v-if="canAddRouting" color="primary" @click="openDialog()"><v-icon left small>mdi-plus</v-icon>Add Routing Rule</v-btn>
      </div>

      <v-data-table
        :loading="isLoading"
        :options.sync="options"
        :headers="table.headers"
        :items="list"
        :items-per-page="table.pageSize"
        :server-items-length="total"
        hide-default-footer
        class="contact-table"
      >
        <template v-slot:item.no="{index, item}">
          {{ rowNo(index, item) }}
        </template>
        <template v-slot:item.org_codename="{item}">
          {{ item.org_codename.toUpperCase() }}
        </template>
        <template v-slot:item.form_type="{item}">
          <span class="type-pill">{{ capitalize(item.form_type) }}</span>
        </template>
        <template v-slot:item.action="{item}">
          <div class="row-actions">
            <button
              class="icon-btn"
              :disabled="!canEditRouting || isRowActionLoading(item.id)"
              @click="openDialog(item)"
            >
              <v-progress-circular
                v-if="actionLoadingType === 'edit' && actionLoadingId === item.id"
                indeterminate
                size="14"
                width="2"
                color="primary"
              />
              <v-icon v-else size="15">mdi-pencil-outline</v-icon>
            </button>
            <button
              class="icon-btn"
              :disabled="!canDeleteRouting || isRowActionLoading(item.id)"
              @click="confirmDelete(item)"
            >
              <v-progress-circular
                v-if="actionLoadingType === 'delete' && actionLoadingId === item.id"
                indeterminate
                size="14"
                width="2"
                color="primary"
              />
              <v-icon v-else size="15">mdi-delete-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>

      <div class="table-foot">
        <span>Showing {{ pageStart }} to {{ pageEnd }} of {{ total }} entries</span>
      </div>
    </v-card>

    <v-dialog v-model="isDialogOpen" max-width="640" persistent>
      <v-card class="routing-modal">
        <v-card-title class="d-flex justify-space-between align-center modal-head">
          <div>
            <div class="modal-title">{{ isEditMode ? 'Edit Routing Rule' : 'Add Routing Rule' }}</div>
            <div class="modal-sub">Emails matching this branch & form type will be sent to recipients below.</div>
          </div>
          <v-btn icon class="close-btn" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-5">
          <v-alert
            v-if="saveErrorMessage"
            type="error"
            dense
            outlined
            class="mb-4"
          >
            {{ saveErrorMessage }}
          </v-alert>

          <label>Hospital Branch</label>
          <v-select
            v-model="form.org_codename"
            :items="orgCodeCreateItems"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="mb-4"
          ></v-select>

          <label>Form Type</label>
          <v-select
            v-model="form.form_type"
            :items="formTypeCreateItems"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="mb-4"
          ></v-select>

          <label>Notification Recipients</label>
          <v-textarea
            v-model="form.notify_email"
            rows="3"
            outlined
            hide-details
          ></v-textarea>
          <div class="hint-text mt-2">Separate multiple email addresses with a comma.</div>
        </v-card-text>

        <v-card-actions class="modal-foot">
          <v-spacer></v-spacer>
          <v-btn outlined @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="isSaveLoading" :disabled="!isFormValid || isSaveLoading" @click="save">Save Rule</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {mapState, mapActions} from 'vuex'
import {hasPermision, hasPermissions} from '@/helper/helper'

const defaultForm = () => ({
  id: null,
  org_codename: 'psuv',
  form_type: 'contact',
  notify_email: ''
})

export default {
  data() {
    return {
      options: {},
      isDialogOpen: false,
      isSaveLoading: false,
      actionLoadingId: null,
      actionLoadingType: '',
      saveErrorMessage: '',
      isEditMode: false,
      form: defaultForm(),
      table: {
        pageSize: 10,
        headers: [
          {text: 'NO.', value: 'no', sortable: false, width: 70},
          {text: 'HOSPITAL BRANCH', value: 'org_codename', sortable: false, width: 180},
          {text: 'FORM TYPE', value: 'form_type', sortable: false, width: 180},
          {text: 'NOTIFICATION RECIPIENTS', value: 'notify_email', sortable: false},
          {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
        ]
      },
      formTypeCreateItems: [
        {text: 'Contact', value: 'contact'},
        {text: 'Feedback', value: 'feedback'},
        {text: 'Complaint', value: 'complaint'}
      ],
      orgCodeCreateItems: [
        {text: 'PSUV', value: 'psuv'},
        {text: 'PPKN', value: 'pkkp'},
        {text: 'PUBN', value: 'psri'}
      ],
      breadcrumbs: [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Contact Form', disabled: true, href: '/contact-form'},
        // {text: 'Email Routing Configuration', disabled: true, href: '/contact-form/routing'}
      ]
    }
  },
  computed: {
    ...mapState('contactRouting', ['list', 'isLoading', 'total', 'currentPage']),
    canViewSubmissionsTab() {
      return hasPermision('CanViewContactMessage')
    },
    canAddRouting() {
      return hasPermision('CanAddContactRoute')
    },
    canEditRouting() {
      return hasPermision('CanEditContactRoute')
    },
    canDeleteRouting() {
      return hasPermision('CanDeleteContactRoute')
    },
    canManageRouting() {
      return hasPermissions([
        'CanViewContactRoute',
        'CanAddContactRoute',
        'CanEditContactRoute',
        'CanDeleteContactRoute'
      ])
    },
    isSubmissionsTab() {
      return this.$route.name === 'contact-form-list'
    },
    isRoutingTab() {
      return this.$route.name === 'contact-form-routing'
    },
    isHospitalInfoTab() {
      return this.$route.name === 'contact-form-hospital-info'
    },
    canViewHospitalContactInfoTab() {
      return hasPermision('CanViewOrganizationContact') || hasPermision('CanEditOrganizationContact')
    },
    isFormValid() {
      return !!(
        this.form &&
        this.form.org_codename &&
        this.form.form_type &&
        this.form.notify_email &&
        this.form.notify_email.trim()
      )
    },
    pageStart() {
      if (!this.total) return 0
      return (this.currentPage - 1) * this.table.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.currentPage * this.table.pageSize, this.total)
    }
  },
  methods: {
    ...mapActions('contactRouting', [
      'fetch',
      'saveRoute',
      'deleteRoute',
      'setPage',
      'setPageSize',
      'setFieldSort',
      'setSort',
      'resetList'
    ]),
     rowNo(index, item) {
      const page = Number(this.currentPage) || 1
      const pageSize = Number(this.table.pageSize) || 10
      let rowIndex = Number(index)

      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = this.list.findIndex((listItem) => {
          if (item && item.id && listItem.id) {
            return String(listItem.id) === String(item.id)
          }
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }

      return (page - 1) * pageSize + rowIndex + 1
    },
    isRowActionLoading(id) {
      return this.actionLoadingId === id
    },
    capitalize(text) {
      return text ? text.charAt(0).toUpperCase() + text.slice(1) : '-'
    },
    async confirmDelete(item) {
      this.actionLoadingId = item.id
      this.actionLoadingType = 'delete'
      if (confirm(`Are you sure you want to delete routing rule for ${item.org_codename.toUpperCase()} (${item.form_type})?`)) {
        const ok = await this.deleteRoute(item.id)
        if (ok) {
          await this.fetch()
        }
      }
      this.actionLoadingId = null
      this.actionLoadingType = ''
    },
    openDialog(item) {
      this.saveErrorMessage = ''

      if (item && !this.canEditRouting) {
        return
      }
      if (!item && !this.canAddRouting) {
        return
      }

      if (item) {
        this.actionLoadingId = item.id
        this.actionLoadingType = 'edit'
      }

      if (item) {
        this.isEditMode = true
        this.form = {
          id: item.id,
          org_codename: item.org_codename,
          form_type: item.form_type,
          notify_email: item.notify_email
        }
      } else {
        this.isEditMode = false
        this.form = defaultForm()
      }
      this.isDialogOpen = true
      this.actionLoadingId = null
      this.actionLoadingType = ''
    },
    closeDialog() {
      this.isDialogOpen = false
      this.saveErrorMessage = ''
      this.form = defaultForm()
    },
    async save() {
      this.isSaveLoading = true
      const result = await this.saveRoute(this.form)
      this.isSaveLoading = false
      if (result && result.ok) {
        this.closeDialog()
        await this.fetch()
      } else {
        this.saveErrorMessage = (result && result.message) || 'Unable to save routing rule'
      }
    },
    goToSubmissionsTab() {
      this.$router.push({name: 'contact-form-list'}).catch(() => {})
    },
    goToHospitalInfoTab() {
      this.$router.push({name: 'contact-form-hospital-info'}).catch(() => {})
    }
  },
  created() {
    if (!this.canManageRouting && this.canViewSubmissionsTab) {
      this.$router.replace({name: 'contact-form-list'}).catch(() => {})
      return
    }
    if (!this.canManageRouting && !this.canViewSubmissionsTab && this.canViewHospitalContactInfoTab) {
      this.$router.replace({name: 'contact-form-hospital-info'}).catch(() => {})
      return
    }
    if (!this.canManageRouting && !this.canViewSubmissionsTab) {
      this.$router.replace({name: 'not-found'}).catch(() => {})
      return
    }

    this.setPage(1)
    this.setPageSize(this.table.pageSize)
    this.fetch()
  },
  beforeRouteLeave(to, from, next) {
    this.resetList()
    next()
  },
  watch: {
    options: {
      handler() {
        const {sortBy, sortDesc, page, itemsPerPage} = this.options
        this.setPage(page || 1)
        this.setFieldSort(sortBy && sortBy[0])
        this.setPageSize(itemsPerPage || this.table.pageSize)
        this.setSort(sortDesc && sortDesc[0])
        this.fetch()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.contact-page { padding: 8px 24px 40px; }
.head-wrap { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; }
.thai { font-size:15px; color:#64748b; font-weight:500; }
.subhead { color:#64748b; font-size:13px; }
.tabs { display:flex; border-bottom:1px solid #e6e9f0; }
.tab { border:none; background:transparent; padding:10px 18px; font-weight:600; color:#64748b; cursor:pointer; }
.tab.active { color:#2f6fed; border-bottom:2px solid #2f6fed; }
.table-card { border:1px solid #e6e9f0; border-radius:10px !important; overflow:hidden; }
.routing-head { display:flex; justify-content:space-between; align-items:center; padding:16px; border-bottom:1px solid #eef2f7; }
.rules-title { font-size:20px; font-weight:700; }
.rules-sub { font-size:13px; color:#64748b; }
.contact-table::v-deep th { font-size:12px !important; color:#64748b !important; }
.contact-table::v-deep td { font-size:14px; }
.type-pill { background:#eef4ff; color:#2f6fed; border-radius:7px; padding:4px 10px; font-size:12px; font-weight:700; }
.row-actions { display:flex; gap:6px; justify-content:center; }
.icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #e6e9f0; background:#fff; }
.table-foot { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; font-size:12px; color:#64748b; }
.routing-modal { border-radius:12px !important; }
.modal-head { border-bottom:1px solid #e6e9f0; }
.modal-title { font-size:30px; font-weight:700; }
.modal-sub { font-size:13px; color:#64748b; }
label { display:block; font-size:11px; color:#94a3b8; text-transform:uppercase; font-weight:700; margin-bottom:6px; }
.hint-text { font-size:11px; color:#94a3b8; }
.modal-foot { border-top:1px solid #e6e9f0; padding:14px 20px; }
.close-btn { background:#f1f5f9; }
</style>
